import { useEffect, useMemo, useRef, useState } from "react";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useCollection,
  useCollections,
  useCurrentUserID,
  useIsOwnSpace,
  usePinnedCollections,
  useRecordsCount,
} from "@/api/hooks";
import {
  CollectionNode,
  buildCollectionTree,
  pathToCollection,
  resolveLevel,
  sortCollectionNodes,
  subtreeStartSeconds,
} from "@/common/collectionTree";
import { HGCollection } from "@/common/types";
import CollectionCreate from "./CollectionCreate";
import ProviderCollectionCreate from "./ProviderCollectionCreate";
import CollectionRow from "./CollectionRow";
import SortableCollectionRow from "./SortableCollectionRow";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "../sidebar";
import { Input } from "../input";
import { Link, useParams } from "react-router";
import CollectionSettingsButton from "./CollectionSettingsButton";
import { ArrowUpDown, ChevronLeft, Search } from "lucide-react";
import { collectionPath } from "@/common/urls";
import { cn } from "@/lib/utils";
import { useCollectionPath } from "@/state/collectionsTree";
import { useShowAllRecords } from "@/common/allRecordsRange";
import {
  useGetCollectionsStartSecondsQuery,
  useReorderCollectionPinsMutation,
  useReorderCollectionsMutation,
} from "@/api/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { CollectionsSort, useRecordsViewOptions } from "@/state/recordsView";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";

// Below this count, filtering isn't worth the extra input taking up space.
const SEARCH_THRESHOLD = 20;

const SORT_LABELS: Record<CollectionsSort, string> = {
  manual: "No sort",
  name: "By name",
  time: "By time",
};

// Header controls match the row targets: thumb-sized in the mobile sheet,
// tight against a pointer.
const ACTION = "size-9 md:size-7 static after:hidden";

/**
 * A row lifted out of its place in the hierarchy to the head of the list — a
 * mirrored system, or a pin: the node it is in the graph, and the path that
 * opens it. What is lifted can be anywhere in the hierarchy, so unlike a row of
 * the level on screen its path is its own, not this level's plus one.
 */
interface LiftedRow {
  node: CollectionNode;
  path: string[];
}

/**
 * The lifted row for a collection, or null when it is nowhere in the forest.
 * A collection that is also one of the rows of the level on screen is one
 * collection and so one row: that node, lifted, rather than a second copy.
 */
function liftRow(
  roots: CollectionNode[],
  nodes: CollectionNode[],
  collection: HGCollection,
): LiftedRow | null {
  const here = nodes.find((node) => node.collection.id === collection.id);
  if (here) return { node: here, path: [collection.id] };
  const ancestors = pathToCollection(roots, collection.id);
  const node = resolveLevel(roots, ancestors).nodes.find(
    (candidate) => candidate.collection.id === collection.id,
  );
  return node ? { node, path: [...ancestors, collection.id] } : null;
}

/** A match in the search list: a row with no hierarchy around it. */
function flatNode(collection: HGCollection): CollectionNode {
  return {
    collection,
    implicit: false,
    key: collection.id,
    children: [],
    descendantCount: 0,
  };
}

export default function CollectionsList() {
  const { userID, collectionID } = useParams();
  const me = useAppSelector(selectMe);
  const collections = useCollections();
  const counts = useRecordsCount();
  // The URL carries a slug, so the active row can only be identified by the
  // resolved collection's real id.
  const activeCollectionID = useCollection()?.id;
  const canPin = useIsOwnSpace();
  const { pinned } = usePinnedCollections();
  // Clicking the already-open collection shows all of its records — widening
  // the time range to fit them, or flipping to the notes tab once the view is
  // already showing everything — instead of navigating to the page we are
  // already on.
  const showAllRecords = useShowAllRecords();
  // The sheet is the whole screen on mobile, so following a link from it has to
  // get out of the way of the page it just opened.
  const { isMobile, setOpenMobile } = useSidebar();
  // Where you are in the hierarchy is remembered per space, so browsing someone
  // else's collections leaves your own position alone.
  const space = useCurrentUserID() ?? "me";
  const { path, goTo } = useCollectionPath(space);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [reorderCollections] = useReorderCollectionsMutation();
  const [reorderPins] = useReorderCollectionPinsMutation();
  // The level's order while a drag settles. The mutation invalidates the
  // collection list, so without this the rows would snap back to the old order
  // for the length of the refetch.
  const [orderedIDs, setOrderedIDs] = useState<string[]>([]);
  // The same, for the pins at the top of the list — they are a second order,
  // written to a different endpoint.
  const [pinnedIDs, setPinnedIDs] = useState<string[]>([]);
  const {
    options: { collectionsSort },
    setOption,
  } = useRecordsViewOptions();
  // Fetched only while sorting by time; nothing else needs it.
  const { currentData: ownStarts } = useGetCollectionsStartSecondsQuery(
    userID,
    { skip: collectionsSort !== "time" },
  );
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const revealedRef = useRef<string | undefined>(undefined);

  const roots = useMemo(() => buildCollectionTree(collections), [collections]);
  const { nodes, trail } = useMemo(
    () => resolveLevel(roots, path),
    [roots, path],
  );
  // Moves are made from the trail that actually resolved rather than from the
  // stored path, so a segment that has gone stale corrects itself on the way
  // rather than leaving "back" pointing at the level already on screen.
  const here = useMemo(() => trail.map((node) => node.collection.id), [trail]);
  const current = trail[trail.length - 1] as CollectionNode | undefined;
  const parent = trail[trail.length - 2] as CollectionNode | undefined;

  const trimmedQuery = query.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!trimmedQuery) return nodes;
    // Search reaches across the whole space, so its results are shown flat:
    // a match is somewhere else in the hierarchy, and following it is what
    // moves the list there.
    return collections
      .filter((collection) =>
        collection.title.toLowerCase().includes(trimmedQuery),
      )
      .map(flatNode);
  }, [collections, nodes, trimmedQuery]);

  // The mirrored systems come first, ahead of the pins and of everything the
  // space has written itself: they are what this space connects, not what it
  // keeps — one per system, created and deleted whole — and reading them as a
  // short list of their own is what keeps Gmail from turning up alphabetically
  // between two collections of notes. Top level only, and out of search, for
  // the reasons the pins below are.
  const providerRows = useMemo<LiftedRow[]>(() => {
    if (trimmedQuery || here.length > 0) return [];
    const found: LiftedRow[] = [];
    for (const collection of collections) {
      // A provider's natural subcollections carry the same `provider_key` as
      // the root they belong to. Only the row that heads a tree is lifted; its
      // children stay one rung down, behind its chevron, where they read as
      // slices of it rather than as systems of their own.
      if (!collection.provider_key || collection.provider_root_id) continue;
      const row = liftRow(roots, nodes, collection);
      if (row) found.push(row);
    }
    return found;
  }, [collections, trimmedQuery, here, nodes, roots]);

  const providerIDs = useMemo(
    () => new Set(providerRows.map((row) => row.node.collection.id)),
    [providerRows],
  );

  // The pins head the collections proper rather than forming a list of their
  // own. Only the top level: inside a collection the list is that collection's
  // children and nothing else, the way the level above it reads. Search is the
  // other exception — the results are what was asked for, and nothing else
  // belongs among them.
  const pinnedKey = pinned.map((collection) => collection.id).join(",");
  const pinnedRows = useMemo<LiftedRow[]>(() => {
    if (!canPin || trimmedQuery || here.length > 0) return [];
    const found: LiftedRow[] = [];
    for (const collection of pinned) {
      // A pinned system is already the first thing in the list. The pin stays
      // togglable on its row up there, but it buys no second row.
      if (providerIDs.has(collection.id)) continue;
      const row = liftRow(roots, nodes, collection);
      if (row) found.push(row);
    }
    return found;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPin, trimmedQuery, pinnedKey, nodes, here, roots, providerIDs]);

  // Re-seed the local orders only when the SET of rows changes — moving to
  // another level, creating, deleting, pinning or unpinning a collection — and
  // not when we ourselves reorder, so a persisted drag does not fight the
  // refetch that follows it.
  const pinnedMembershipKey = pinnedRows
    .map((row) => row.node.collection.id)
    .sort()
    .join(",");
  useEffect(() => {
    setPinnedIDs(pinnedRows.map((row) => row.node.collection.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedMembershipKey]);

  const pinnedByID = new Map(
    pinnedRows.map((row) => [row.node.collection.id, row]),
  );
  const reordered = pinnedIDs
    .map((id) => pinnedByID.get(id))
    .filter((row): row is LiftedRow => Boolean(row));
  // Only a genuine permutation is used: anything else means the local order
  // belongs to a set of pins one render old.
  const pinnedList =
    reordered.length === pinnedRows.length ? reordered : pinnedRows;
  const pinnedListKey = pinnedList
    .map((row) => row.node.collection.id)
    .join(",");
  const pinnedOnScreen = useMemo(
    () => new Set(pinnedListKey ? pinnedListKey.split(",") : []),
    [pinnedListKey],
  );

  // What is left for the level itself: a row lifted to the head of the list —
  // a mirrored system, a pin — is listed once, up there, and not again in its
  // place further down.
  //
  // Sorted after the lifting: the systems and the pins keep their own order
  // above, and only the level itself follows the viewer's sort. Until the start
  // seconds arrive a time sort shows the manual order rather than a guess.
  const levelRows = useMemo(() => {
    const remaining = rows.filter(
      (node) =>
        !providerIDs.has(node.collection.id) &&
        !pinnedOnScreen.has(node.collection.id),
    );
    if (collectionsSort === "time" && !ownStarts) return remaining;
    const starts =
      collectionsSort === "time" && ownStarts
        ? subtreeStartSeconds(
            remaining.map((node) => node.collection.id),
            collections,
            ownStarts,
          )
        : new Map<string, bigint>();
    return sortCollectionNodes(remaining, collectionsSort, starts);
  }, [
    rows,
    providerIDs,
    pinnedOnScreen,
    collectionsSort,
    ownStarts,
    collections,
  ]);

  // Reordering is the owner's own arrangement of one level, so it is off in
  // somebody else's space and off in search results — a match is shown flat,
  // out of the hierarchy, and dropping it "between" two collections it is not
  // a sibling of would mean nothing. It is off under a sort too: the drop would
  // be saved, but the list would not show it.
  const canReorder =
    canPin &&
    collectionsSort === "manual" &&
    !trimmedQuery &&
    levelRows.length > 1;
  const canReorderPins = pinnedList.length > 1;

  const membershipKey = levelRows
    .map((node) => node.collection.id)
    .sort()
    .join(",");
  useEffect(() => {
    setOrderedIDs(levelRows.map((node) => node.collection.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipKey]);

  const byID = new Map(levelRows.map((node) => [node.collection.id, node]));
  const dragged = orderedIDs
    .map((id) => byID.get(id))
    .filter((node): node is CollectionNode => Boolean(node));
  // Only a genuine permutation of this level is used: anything else means the
  // local order belongs to the level we were on a render ago.
  const listRows =
    canReorder && dragged.length === levelRows.length ? dragged : levelRows;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeID = active.id as string;
    const overID = over.id as string;
    // The pins and the level are two separate orders sharing one list. A row
    // dragged from one into the other says nothing either of them can record,
    // so the drop is dropped.
    const fromPins = pinnedOnScreen.has(activeID);
    if (fromPins !== pinnedOnScreen.has(overID)) return;

    if (fromPins) {
      const next = arrayMove(
        pinnedIDs,
        pinnedIDs.indexOf(activeID),
        pinnedIDs.indexOf(overID),
      );
      setPinnedIDs(next);
      reorderPins(next);
      return;
    }

    const oldIndex = orderedIDs.indexOf(activeID);
    const newIndex = orderedIDs.indexOf(overID);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIDs, oldIndex, newIndex);
    setOrderedIDs(next);
    // The whole level goes, not just the two rows that swapped: until the
    // first drag none of them has a position at all, and a level half-placed
    // would interleave with the alphabetical remainder. That includes the
    // level's own rows currently showing above — they are out of the dragged
    // list, and leaving them out of the write would drop them back among the
    // unplaced ones the moment they stop being lifted.
    const lifted = rows
      .map((node) => node.collection.id)
      .filter((id) => providerIDs.has(id) || pinnedOnScreen.has(id));
    reorderCollections([...lifted, ...next]);
  };

  // Either listed at this level, or the collection this level belongs to.
  // The pins are not part of this: a pinned collection is on screen at the top
  // level whatever it is nested under, and counting it would leave the list
  // sitting at the top with the collection just opened not shown in its place.
  const activeIsOnScreen =
    !!activeCollectionID &&
    (current?.collection.id === activeCollectionID ||
      nodes.some((node) => node.collection.id === activeCollectionID));

  useEffect(() => {
    // Only one level is on screen, so the collection just navigated to is very
    // likely not on it. Keyed on a ref rather than on `path`: re-running would
    // drag the list back from wherever the user has since browsed to.
    if (!activeCollectionID || roots.length === 0) return;
    if (revealedRef.current === activeCollectionID) return;
    revealedRef.current = activeCollectionID;
    // Opening the collection whose children are showing must not walk back out
    // of it, and a row already on screen needs no revealing.
    if (activeIsOnScreen) return;
    // Otherwise the level that *lists* it — its ancestors — not the collection
    // itself, which would open onto its children and hide the row being
    // revealed.
    goTo(pathToCollection(roots, activeCollectionID));
  }, [activeCollectionID, activeIsOnScreen, roots, goTo]);

  useEffect(() => {
    // A space's own page is the top of its collection tree: whatever level the
    // sidebar was showing belongs to wherever the user browsed before, not to
    // the page just opened. Keyed on the route, so browsing down from here —
    // which changes no segment — is left alone. The reveal above is unkeyed
    // with it so returning to the collection last visited moves the list back
    // to it rather than counting as already revealed.
    if (collectionID) return;
    revealedRef.current = undefined;
    goTo([]);
  }, [userID, collectionID, goTo]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const showSearch = collections.length > SEARCH_THRESHOLD;

  // Where a new collection should land: inside the open one when it is a row
  // the user can actually see, and otherwise inside the collection whose
  // children these rows are — so the new collection turns up on the level
  // being looked at rather than under whatever the URL happens to point at.
  // Null at the top level with nothing open there, which is a root collection.
  const createParent =
    rows.find((node) => node.collection.id === activeCollectionID)
      ?.collection ??
    current?.collection ??
    null;

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const handleToggleSearch = () => {
    if (searchOpen) closeSearch();
    else setSearchOpen(true);
  };

  /** A link in this list was followed: get out of its way. */
  const handleNavigate = () => {
    // A match is shown flat, so leaving the results is what lets the reveal
    // above put the collection back in its place in the hierarchy.
    if (trimmedQuery) closeSearch();
    if (isMobile) setOpenMobile(false);
  };

  const goUp = current ? () => goTo(here.slice(0, -1)) : undefined;

  // One row, drawn by whichever of the two components this level calls for.
  // SortableCollectionRow is the same row plus a `useSortable` — which is a
  // hook, and so cannot be the thing a per-level flag switches off.
  const Row: typeof CollectionRow = canReorder
    ? SortableCollectionRow
    : CollectionRow;
  const PinRow: typeof CollectionRow = canReorderPins
    ? SortableCollectionRow
    : CollectionRow;

  const renderRow = (node: CollectionNode) => (
    <Row
      key={node.key}
      node={node}
      isActive={activeCollectionID === node.collection.id}
      count={counts[node.collection.id]}
      canPin={canPin}
      onDrill={() => goTo([...here, node.collection.id])}
      onNavigate={handleNavigate}
      onUp={goUp}
      onActiveClick={showAllRecords}
    />
  );

  // A lifted row drills into its own place in the hierarchy, which is where
  // the list moves — the level it heads is not necessarily the level below it.
  const renderLiftedRow = (
    { node, path: rowPath }: LiftedRow,
    Component: typeof CollectionRow,
  ) => (
    <Component
      key={node.key}
      node={node}
      isActive={activeCollectionID === node.collection.id}
      count={counts[node.collection.id]}
      canPin={canPin}
      onDrill={() => goTo(rowPath)}
      onNavigate={handleNavigate}
      onUp={goUp}
      onActiveClick={showAllRecords}
    />
  );

  const emptyMessage = trimmedQuery
    ? "No matches"
    : current
      ? "Nothing inside"
      : // What is lifted above is collections too, so "none" would be a lie:
        // what the top level has none of is collections not already listed.
        pinnedList.length > 0 || providerRows.length > 0
        ? "Nothing else at the top level"
        : "No collections";

  // The one mark of where a lifted group ends. It is all a single list — the
  // systems and the pins are not boxes of their own — but a row standing here
  // out of its place in the hierarchy would otherwise read as a root.
  const hairline = (
    <li aria-hidden className="border-b border-sidebar-border" />
  );
  const providerDivider = providerRows.length > 0 && hairline;
  const pinDivider = pinnedList.length > 0 && hairline;

  const levelSection =
    levelRows.length === 0 ? (
      <SidebarMenuItem className="px-3 py-2 text-[13px] text-muted-foreground">
        {emptyMessage}
      </SidebarMenuItem>
    ) : (
      listRows.map(renderRow)
    );

  // Signed out and in nobody's space, there is nothing to list and never will
  // be: GET /collections answers [] for a session-less request that names no
  // user. The header, the empty-state line and the create button would all be
  // furniture around a permanent void, so the panel stays blank until the
  // visitor picks someone from the switcher above it. (After all the hooks:
  // this is a decision about what to draw, not about what to compute.)
  if (!me && !userID) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">
      <SidebarGroup className="grow gap-1 overflow-auto p-0 pt-2">
        {/* The one place that says where in the hierarchy this level sits: the
            way back out, and the collection whose children are listed. */}
        <div className="flex shrink-0 items-center gap-1 px-2">
          {current ? (
            <>
              <button
                type="button"
                onClick={goUp}
                aria-label={
                  parent
                    ? `Back to ${parent.collection.title}`
                    : "Back to all collections"
                }
                className={cn(
                  "flex shrink-0 items-center justify-center rounded-md text-muted-foreground",
                  "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  ACTION,
                )}
              >
                <ChevronLeft className="size-4" />
              </button>
              <Link
                to={collectionPath(current.collection)}
                aria-current={
                  activeCollectionID === current.collection.id
                    ? "page"
                    : undefined
                }
                onClick={(event) => {
                  if (activeCollectionID === current.collection.id) {
                    if (!showAllRecords) return;
                    event.preventDefault();
                    showAllRecords();
                    return;
                  }
                  handleNavigate();
                }}
                className={cn(
                  "min-w-0 flex-1 truncate text-[13px] font-semibold hover:underline",
                  activeCollectionID === current.collection.id
                    ? "text-primary"
                    : "text-sidebar-foreground",
                )}
              >
                {current.collection.title}
              </Link>
            </>
          ) : (
            <span className="min-w-0 flex-1 truncate px-2 text-[10.5px] font-bold uppercase tracking-[.1em] text-sidebar-foreground/70">
              Collections
            </span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Sort collections: ${SORT_LABELS[collectionsSort]}`}
                title={`Sort: ${SORT_LABELS[collectionsSort]}`}
                className={cn(
                  "flex shrink-0 items-center justify-center rounded-md",
                  "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  collectionsSort === "manual"
                    ? "text-sidebar-foreground"
                    : "text-primary",
                  ACTION,
                )}
              >
                <ArrowUpDown className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-36">
              <DropdownMenuLabel className="text-[12px] text-muted-foreground">
                Sort collections
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={collectionsSort}
                onValueChange={(value) =>
                  setOption("collectionsSort", value as CollectionsSort)
                }
              >
                {(Object.keys(SORT_LABELS) as CollectionsSort[]).map(
                  (value) => (
                    <DropdownMenuRadioItem
                      key={value}
                      value={value}
                      className="text-[13px]"
                    >
                      {SORT_LABELS[value]}
                    </DropdownMenuRadioItem>
                  ),
                )}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          {showSearch && (
            <button
              type="button"
              aria-label={
                searchOpen ? "Hide collection search" : "Search collections"
              }
              aria-expanded={searchOpen}
              onClick={handleToggleSearch}
              className={cn(
                "flex shrink-0 items-center justify-center rounded-md text-sidebar-foreground",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                ACTION,
              )}
            >
              <Search className="size-4" />
            </button>
          )}
          {/* Mirroring a system is always a root of its own, so it takes no
              parent: unlike a collection of your own, there is nothing about
              the level on screen that should change what gets created. */}
          <ProviderCollectionCreate className={ACTION} />
          <CollectionCreate className={ACTION} parent={createParent} />
        </div>
        {showSearch && searchOpen && (
          <div className="px-2 pb-0.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search collections..."
                className="h-9 pl-7 text-[13px] md:h-7"
              />
            </div>
          </div>
        )}
        <SidebarGroupContent className="h-full overflow-auto noscroll border-t border-sidebar-border">
          <SidebarMenu className="gap-0">
            {providerRows.map((row) => renderLiftedRow(row, CollectionRow))}
            {providerDivider}
            {canReorder || canReorderPins ? (
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={pinnedIDs}
                  strategy={verticalListSortingStrategy}
                >
                  {pinnedList.map((row) => renderLiftedRow(row, PinRow))}
                </SortableContext>
                {pinDivider}
                <SortableContext
                  items={orderedIDs}
                  strategy={verticalListSortingStrategy}
                >
                  {levelSection}
                </SortableContext>
              </DndContext>
            ) : (
              <>
                {pinnedList.map((row) => renderLiftedRow(row, PinRow))}
                {pinDivider}
                {levelSection}
              </>
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <SidebarGroup className="gap-2">
        <CollectionSettingsButton />
      </SidebarGroup>
    </div>
  );
}
